import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, language, setLanguage, availableLanguages } = useLanguage();
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const navItems = [
    { key: 'elCamp', path: '/el-camp' },
    { key: 'jugar', path: '/jugar' },
    { key: 'competicio', path: '/competicio' },
    { key: 'iniciacio', path: '/iniciacio' },
    { key: 'drivingRange', path: '/driving-range' },
    { key: 'restaurant', path: '/restaurant' },
    { key: 'allotjament', path: '/allotjament' },
    { key: 'correspondencies', path: '/correspondencies' },
    { key: 'contacte', path: '/contacte' },
    { key: 'equip', path: '/equip' },
    { key: 'premsa', path: '/premsa' },
    // { key: 'areaMembres', path: '/membres' }, // UNPUBLISHED - Re-enable when needed
  ];

  const isActive = (path) => location.pathname === path;

  const langLabels = { ca: 'CA', es: 'ES', fr: 'FR', en: 'EN' };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-[0_4px_24px_rgba(0,0,0,0.06)]'
          : 'bg-transparent'
      }`}
    >
      {/* Gradient scrim for readability on light images - only visible when not scrolled */}
      <div 
        className={`absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-transparent transition-opacity duration-300 pointer-events-none ${
          isScrolled ? 'opacity-0' : 'opacity-100'
        }`}
        aria-hidden="true"
      />
      
      <div className="container-custom relative">
        <div className="flex items-center justify-between h-16 sm:h-20 md:h-24 lg:h-28">
          {/* Logo - Presència institucional premium */}
          <Link 
            to="/" 
            className="flex-shrink-0 z-10"
            data-testid="header-logo"
          >
            {/* Logo blanc quan header transparent, verd quan scrolled */}
            <img 
              src="https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/ibb3s625_UGOLF%20ARAVELL%20-%20ANDORRA%20-%20BLANCO.svg"
              alt="UGOLF Aravell Andorra"
              className={`h-8 sm:h-10 md:h-12 lg:h-[4.5rem] w-auto transition-all duration-300 ${
                isScrolled ? 'brightness-0' : 'brightness-100'
              }`}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-1" data-testid="desktop-nav">
            {navItems.slice(0, 6).map((item) => (
              <Link
                key={item.key}
                to={item.path}
                data-testid={`nav-${item.key}`}
                className={`px-3 py-2 text-xs tracking-[0.1em] uppercase font-medium transition-colors duration-200 ${
                  isActive(item.path)
                    ? isScrolled ? 'text-[#1A4D2E]' : 'text-white'
                    : isScrolled 
                      ? 'text-[#666666] hover:text-[#1A4D2E]' 
                      : 'text-white/80 hover:text-white'
                }`}
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}
            
            {/* More dropdown for remaining items */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className={`px-3 py-2 text-xs tracking-[0.1em] uppercase font-medium flex items-center gap-1 transition-colors duration-200 ${
                    isScrolled 
                      ? 'text-[#666666] hover:text-[#1A4D2E]' 
                      : 'text-white/80 hover:text-white'
                  }`}
                  data-testid="nav-more-dropdown"
                >
                  Més <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white border border-[#E5E5E5]">
                {navItems.slice(6).map((item) => (
                  <DropdownMenuItem key={item.key} asChild>
                    <Link
                      to={item.path}
                      data-testid={`nav-${item.key}`}
                      className="text-xs tracking-[0.1em] uppercase font-medium text-[#666666] hover:text-[#1A4D2E] cursor-pointer"
                    >
                      {t(`nav.${item.key}`)}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-3 lg:gap-4">
            {/* Phone - hidden on mobile */}
            <a
              href="tel:+34661194023"
              className={`hidden md:flex items-center gap-2 text-sm font-medium transition-colors duration-200 ${
                isScrolled ? 'text-[#1A4D2E]' : 'text-white'
              }`}
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden lg:inline">+34 661 19 40 23</span>
            </a>

            {/* Language selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className={`px-2 py-1 text-xs font-semibold tracking-wider uppercase flex items-center gap-1 transition-colors duration-200 ${
                    isScrolled ? 'text-[#1A4D2E]' : 'text-white'
                  }`}
                  data-testid="lang-selector"
                >
                  {langLabels[language]} <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[80px] bg-white border border-[#E5E5E5]">
                {availableLanguages.map((lang) => (
                  <DropdownMenuItem
                    key={lang}
                    onClick={() => setLanguage(lang)}
                    className={`text-xs font-semibold tracking-wider uppercase cursor-pointer ${
                      language === lang ? 'text-[#1A4D2E] bg-[#F0F2EF]' : 'text-[#666666]'
                    }`}
                    data-testid={`lang-${lang}`}
                  >
                    {langLabels[lang]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* LeClub micro-icon */}
            <a
              href="https://leclub-golf.com/es"
              target="_blank"
              rel="noopener noreferrer"
              className={`hidden lg:flex items-center transition-opacity duration-200 hover:opacity-100 ${
                isScrolled ? 'opacity-80' : 'opacity-70'
              }`}
              title="Membre LeClub Golf"
              data-testid="header-leclub"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/6s8tzwbl_Leclub.svg"
                alt="LeClub Golf"
                className={`h-9 w-auto transition-all duration-300 ${
                  isScrolled ? 'brightness-0' : 'brightness-0 invert'
                }`}
              />
            </a>

            {/* Reserva button */}
            <a
              href="https://open.teeone.golf/es/aravell"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:block bg-[#1A4D2E] text-white px-4 lg:px-6 py-2.5 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#153d24] transition-colors duration-200"
              data-testid="header-reserva-btn"
            >
              {t('nav.reserva')}
            </a>

            {/* Mobile menu trigger */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button 
                  className={`xl:hidden p-2 transition-colors duration-200 ${
                    isScrolled ? 'text-[#1A4D2E]' : 'text-white'
                  }`}
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-[400px] bg-white p-0">
                <div className="flex flex-col h-full">
                  {/* Mobile menu header */}
                  <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5]">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/ibb3s625_UGOLF%20ARAVELL%20-%20ANDORRA%20-%20BLANCO.svg"
                      alt="UGOLF Aravell Andorra"
                      className="h-8 w-auto brightness-0"
                    />
                  </div>

                  {/* Navigation items */}
                  <nav className="flex-1 overflow-y-auto py-4">
                    {navItems.map((item, index) => (
                      <motion.div
                        key={item.key}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          data-testid={`mobile-nav-${item.key}`}
                          className={`block px-6 py-3 text-sm tracking-[0.1em] uppercase font-medium border-b border-[#F0F2EF] transition-colors duration-200 ${
                            isActive(item.path)
                              ? 'text-[#1A4D2E] bg-[#F0F2EF]'
                              : 'text-[#666666] hover:text-[#1A4D2E] hover:bg-[#F9F9F7]'
                          }`}
                        >
                          {t(`nav.${item.key}`)}
                        </Link>
                      </motion.div>
                    ))}
                  </nav>

                  {/* Mobile menu footer */}
                  <div className="p-6 border-t border-[#E5E5E5] space-y-4">
                    {/* Language selector mobile */}
                    <div className="flex items-center gap-2">
                      {availableLanguages.map((lang) => (
                        <button
                          key={lang}
                          onClick={() => setLanguage(lang)}
                          className={`px-3 py-1.5 text-xs font-semibold tracking-wider uppercase transition-colors duration-200 ${
                            language === lang
                              ? 'bg-[#1A4D2E] text-white'
                              : 'bg-[#F0F2EF] text-[#666666] hover:bg-[#E5E5E5]'
                          }`}
                          data-testid={`mobile-lang-${lang}`}
                        >
                          {langLabels[lang]}
                        </button>
                      ))}
                    </div>

                    {/* Contact info */}
                    <a
                      href="tel:+34661194023"
                      className="flex items-center gap-2 text-[#1A4D2E] font-medium"
                    >
                      <Phone className="w-4 h-4" />
                      +34 661 19 40 23
                    </a>

                    {/* Reserva button */}
                    <a
                      href="https://open.teeone.golf/es/aravell"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full bg-[#1A4D2E] text-white text-center py-3 text-sm font-semibold tracking-[0.15em] uppercase hover:bg-[#153d24] transition-colors duration-200"
                      data-testid="mobile-reserva-btn"
                    >
                      {t('nav.reserva')}
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
